/* eslint-disable css-modules/no-undef-class, css-modules/no-unused-class, no-nested-ternary */
import { FC, useEffect, useState } from 'react';
import { Locale, translate } from 'translations';
import { AccordionTable, Avatar, CollapseTableData, Icon } from 'learning-panel-components';
import classNames from 'classnames';
import { getLocaleDateString, getPlatformUrl } from 'utils';
import styles from './assignedContentFullExpanded.module.scss';

const AssignedContentFullExpanded: FC<AssignedContentFullExpandedProps> = (props: AssignedContentFullExpandedProps) => {
    const { onUnExpand, onClose, assignedLessons, assignedAssessments, primaryCourse, locale } = props;
    const [coursesRows, setCoursesRows] = useState<CollapseTableData[]>([]);
    const [lessonsRows, setLessonsRows] = useState<CollapseTableData[]>([]);
    const [assessmentsRows, setAssessmentsRows] = useState<CollapseTableData[]>([]);
    const [tablesHeight, setTablesHeight] = useState<(string | undefined)[]>([undefined, undefined]);

    const lessonNameText = translate('lessonName');
    const courseNameText = translate('courseName');
    const assessmentNameText = translate('assessmentName');
    const dateText = translate('Date');
    const devCoinsText = translate('DevCoins');
    const dueDateText = translate('DueDate');

    const emptyIconElement = <div className={styles['empty-icon']}> </div>;
    const getTitleElement = (texts: string[]) =>
        texts.map((text: string) => (
            <div key={text} className={styles['title-element']}>
                {text}
            </div>
        ));
    const getTitleRow = (texts: string[]) => [emptyIconElement, ...getTitleElement(texts)];
    const getIconElement = (isDone: boolean) =>
        isDone ? <Icon name="contentLineDone" className={styles['done-icon']} /> : emptyIconElement;

    const getClassName = (baseClass: string, isDone: boolean) =>
        classNames(styles[baseClass], { [styles['done-row']]: isDone });

    const getRow = (row: Row) => {
        const { isDone } = row;
        const iconElement = getIconElement(isDone);
        const nameElement = (
            <div className={getClassName('row-name', isDone)}>
                {row.iconUrl ? (
                    <Avatar key={row.iconUrl} name={''} avatarSrc={row.iconUrl}></Avatar>
                ) : (
                    <Icon name="contentLine" />
                )}
                <div className={styles.text}>
                    <a href={row.url} title={row.name}>
                        {row.name}
                    </a>
                </div>
            </div>
        );
        const dateElement = (
            <div className={getClassName('row-date', isDone)}>
                {row.date ? getLocaleDateString(row.date, locale) : '--'}
            </div>
        );
        const scoreElement = (
            <div className={getClassName('row-score', isDone)}>
                <div className={styles.devCoin}>{row.devCoins}</div>
            </div>
        );
        return [iconElement, nameElement, dateElement, scoreElement];
    };

    useEffect(() => {
        const tmpRows: CollapseTableData[] = [
            { row: getTitleRow([courseNameText, dateText, devCoinsText]) },
            {
                row: getRow({
                    isDone: primaryCourse.completionPercentage === 100,
                    name: primaryCourse.displayName,
                    date: primaryCourse.assignedOn,
                    devCoins: primaryCourse.score?.toLocaleString(),
                    url: `${getPlatformUrl()}/app/course?courseUuid=${primaryCourse.uuid}`,
                    iconUrl: primaryCourse.iconUrl
                })
            }
        ];
        setCoursesRows(tmpRows);
    }, [primaryCourse]);

    useEffect(() => {
        const tmpRows: CollapseTableData[] = [{ row: getTitleRow([lessonNameText, dateText, devCoinsText]) }];
        assignedLessons.lessons.map((lesson: any) => {
            tmpRows.push({
                row: getRow({
                    isDone: lesson.status === 'completed',
                    name: lesson.displayName,
                    date: lesson.assignedAt,
                    devCoins: lesson.score?.toLocaleString(),
                    url: `${getPlatformUrl()}${lesson.url}`,
                    iconUrl: lesson.iconUrl
                })
            });
        });

        setLessonsRows(tmpRows);
    }, [assignedLessons]);

    useEffect(() => {
        const tmpRows: CollapseTableData[] = [{ row: getTitleRow([assessmentNameText, dueDateText, devCoinsText]) }];
        assignedAssessments.assessments.map((assessment: any) => {
            tmpRows.push({
                row: getRow({
                    isDone: assessment.status === 'completed',
                    name: assessment.name,
                    date: assessment.endDate,
                    devCoins: assessment.score?.toLocaleString(),
                    url: `${getPlatformUrl()}${assessment.url}`
                })
            });
        });

        setAssessmentsRows(tmpRows);
    }, [assignedAssessments]);

    useEffect(() => {
        let rowNumbers = 0;
        if (lessonsRows.length) rowNumbers++;
        if (assessmentsRows.length) rowNumbers++;
        rowNumbers += lessonsRows.length;
        rowNumbers += assessmentsRows.length;
        if (rowNumbers <= 5) return;
        if (!lessonsRows.length) setTablesHeight([undefined, '22.25rem']);
        if (!assessmentsRows.length) setTablesHeight(['22.25rem', undefined]);
        setTablesHeight(['12.5rem', '12.5rem']);
    }, [lessonsRows, assessmentsRows]);
    return (
        <div data-testid="assigned-content" className={styles['assigned-content']}>
            <div className={styles.top}>
                <div className={styles.title}>{translate('AssignedContent')}</div>
                <div className={styles['icons-wrapper']}>
                    <div
                        onClick={() => {
                            onUnExpand(true);
                        }}
                    >
                        <Icon data-testid="arrow-icon" name={'unExpandIcon'} className={styles.icon} />
                    </div>
                    <div onClick={() => onClose(false)}>
                        <Icon data-testid="arrow-icon" name={'close'} className={styles.icon} />
                    </div>
                </div>
            </div>
            <hr />
            <div className={styles.content}>
                {primaryCourse && (
                    <div className={styles.table}>
                        <AccordionTable
                            rows={coursesRows}
                            title={translate('Courses')}
                            titleIconName="courseIconLearningPanel"
                            isOpen={true}
                            hasMoreRows={false}
                            titleChartPercentage={primaryCourse.completionPercentage}
                            newTableLayout
                        />
                    </div>
                )}
                {assignedLessons && (
                    <div className={styles.table}>
                        <AccordionTable
                            rows={lessonsRows}
                            title={translate('Lessons')}
                            isOpen={true}
                            hasMoreRows={false}
                            titleIconName="lessonIconLearningPanel"
                            titleCount={assignedLessons.total}
                            titleChartPercentage={assignedLessons.completionPercentage}
                            tableHeight={tablesHeight[0]}
                            isEmpty={lessonsRows.length === 1}
                            emptyText={translate('noAssignedLessonsMessage')}
                            emptyIconName="coffee"
                            newTableLayout
                        />
                    </div>
                )}
                {assignedAssessments && (
                    <AccordionTable
                        rows={assessmentsRows}
                        title={translate('Assessments')}
                        isOpen={true}
                        hasMoreRows={false}
                        titleIconName="assessmentIconLearningPanel"
                        titleCount={assignedAssessments.total}
                        titleChartPercentage={assignedAssessments.completionPercentage}
                        tableHeight={tablesHeight[1]}
                        isEmpty={assessmentsRows.length === 1}
                        emptyText={translate('noAssignedLessonsMessage')}
                        emptyIconName="coffee"
                        newTableLayout
                    />
                )}
            </div>
        </div>
    );
};

interface AssignedContentFullExpandedProps {
    isLoading: boolean;
    onUnExpand: Function;
    onClose: Function;
    primaryCourse?: any;
    assignedLessons?: any;
    assignedAssessments?: any;
    locale: keyof typeof Locale;
}

interface Row {
    isDone: boolean;
    name: string;
    date: Date;
    devCoins?: number;
    url: string;
    iconUrl?: string;
}

export default AssignedContentFullExpanded;
